export type LoadingState<T> =
  | { isLoading: true; isLoaded: false; value?: undefined }
  | { isLoading: false; isLoaded: true; value: T }

/**
 * A helper to annotate a value with loading state based on a boolean.
 *
 * Returns {isLoading: boolean, isLoaded: boolean, value: T}, except that T is narrowed when the loading state is checked.
 */
export function guardOn<T>(
  condition: boolean,
  computeFn: () => T
): LoadingState<T> {
  if (!condition) {
    return { isLoading: true, isLoaded: false }
  }
  return { isLoading: false, isLoaded: true, value: computeFn() }
}

/**
 * A helper to annotate a value with loading state based on other loading states.
 *
 * Can be passed an object or an array of objects.
 *
 * Returns {isLoading: boolean, isLoaded: boolean, value: T}, except that T is narrowed when the loading state is checked.
 */
export function guardOnLoaded<
  Sources extends readonly LoadableSource<unknown>[],
  T,
>(
  sources: [...Sources],
  computeFn: (values: ExtractValues<Sources>) => T
): LoadingState<T>

export function guardOnLoaded<S extends LoadableSource<unknown>, T>(
  source: S,
  computeFn: (value: PayloadOf<S>) => T
): LoadingState<T>

export function guardOnLoaded<T>(
  sourceOrSources:
    | LoadableSource<unknown>
    | readonly (boolean | LoadableSource<unknown>)[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  computeFn: (arg?: any) => T
): LoadingState<T> {
  if (Array.isArray(sourceOrSources)) {
    const sourcesArr = sourceOrSources
    if (sourcesArr.some((src) => src.isLoading)) {
      return { isLoading: true, isLoaded: false }
    }

    const values = sourcesArr.map(extractPayload) as ExtractValues<
      typeof sourcesArr
    >

    return { isLoading: false, isLoaded: true, value: computeFn(values) }
  }

  // sourceOrSources is now narrowed
  const source = sourceOrSources as LoadableSource<unknown>

  if (source.isLoading) {
    return { isLoading: true, isLoaded: false }
  }

  const resolvedValue = extractPayload(source) as ComputeFnArg<unknown>

  return { isLoading: false, isLoaded: true, value: computeFn(resolvedValue) }
}

function extractPayload(source: LoadableSource<unknown>): unknown {
  if ('value' in source) return source.value
  if ('models' in source) return source.models
  if ('data' in source) return source.data
  return undefined
}

export type ComputeFnArg<S> = S extends readonly LoadableSource<unknown>[]
  ? ExtractValues<S>
  : S extends LoadableSource<unknown>
    ? PayloadOf<S>
    : never

type LoadableSource<T> = {
  isLoading: boolean
  isLoaded: boolean
} & (
  | { value: T; models?: never; data?: never }
  | { models: T; value?: never; data?: never }
  | { data: T; value?: never; models?: never }
  | { value?: undefined; models?: undefined; data?: undefined }
)

type ExtractValues<Sources extends readonly LoadableSource<unknown>[]> = {
  [K in keyof Sources]: PayloadOf<Sources[K]>
}

type PayloadOf<S> = S extends { value: infer V }
  ? V
  : S extends { models: infer M }
    ? M
    : S extends { data: infer D }
      ? D
      : never
